class GleamvoraTheme {
  constructor() {
    this.body = document.body;
    this.bindDrawers();
    this.bindQuantity();
    this.bindProductGallery();
    this.bindProductForms();
    this.bindVariantSelectors();
    this.bindSort();
    this.bindFilterToggle();
    this.bindStickyProduct();
    this.bindBackLinks();
    this.bindSiteFavicon();
  }

  bindDrawers() {
    document.addEventListener('click', (event) => {
      const opener = event.target.closest('[data-open-drawer]');
      const closer = event.target.closest('[data-close-drawer]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener.dataset.openDrawer);
      }
      if (closer) {
        event.preventDefault();
        this.closeDrawers();
      }
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.closeDrawers();
    });
  }

  openDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    this.closeDrawers();
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    this.body.classList.add('drawer-open');
    drawer.querySelector('input,button,a')?.focus();
  }

  closeDrawers() {
    document.querySelectorAll('.mobile-drawer.is-open,.search-drawer.is-open,.cart-drawer.is-open').forEach((drawer) => {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    });
    this.body.classList.remove('drawer-open');
  }

  bindQuantity() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-quantity-button]');
      if (!button) return;
      const wrapper = button.closest('.quantity');
      const input = wrapper?.querySelector('input[type="number"]');
      if (!input) return;
      const step = Number(input.step || 1);
      const min = Number(input.min || 0);
      const next = button.dataset.quantityButton === 'plus' ? Number(input.value) + step : Number(input.value) - step;
      input.value = Math.max(min, next);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  bindProductGallery() {
    document.addEventListener('click', (event) => {
      const thumb = event.target.closest('[data-product-thumbnail]');
      if (thumb) {
        const gallery = thumb.closest('[data-product-gallery]');
        const main = gallery?.querySelector('[data-product-main-image]');
        if (main) {
          main.src = thumb.dataset.imageSrc;
          main.srcset = '';
          main.alt = thumb.dataset.imageAlt || '';
          gallery.querySelectorAll('[data-product-thumbnail]').forEach((item) => item.classList.remove('is-active'));
          thumb.classList.add('is-active');
        }
      }
      const zoom = event.target.closest('[data-image-zoom]');
      if (zoom) {
        const dialog = document.getElementById(zoom.dataset.imageZoom);
        const image = dialog?.querySelector('img');
        const main = zoom.querySelector('img');
        if (dialog && image && main) {
          image.src = main.currentSrc || main.src;
          image.alt = main.alt;
          dialog.showModal();
        }
      }
      if (event.target.closest('[data-close-modal]')) event.target.closest('dialog')?.close();
    });
  }

  bindProductForms() {
    document.addEventListener('submit', async (event) => {
      const form = event.target.closest('.js-product-form');
      if (!form) return;
      event.preventDefault();
      const button = form.querySelector('[type="submit"]');
      const oldText = button?.textContent;
      if (button) { button.disabled = true; button.textContent = 'Adding…'; }
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: new FormData(form)
        });
        if (!response.ok) throw new Error((await response.json()).description || 'Unable to add item');
        await this.refreshCartDrawer();
        this.openDrawer('CartDrawer');
      } catch (error) {
        alert(error.message);
      } finally {
        if (button) { button.disabled = false; button.textContent = oldText; }
      }
    });
  }

  async refreshCartDrawer() {
    const response = await fetch(`${window.location.pathname}?section_id=cart-drawer`);
    const html = await response.text();
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const replacement = parsed.querySelector('#CartDrawer');
    const current = document.querySelector('#CartDrawer');
    if (replacement && current) current.replaceWith(replacement);
    const count = replacement?.dataset.cartCount || '0';
    document.querySelectorAll('[data-cart-count]').forEach((el) => el.textContent = count);
  }

  bindVariantSelectors() {
    document.querySelectorAll('[data-variant-select]').forEach((select) => {
      select.addEventListener('change', () => {
        const option = select.options[select.selectedIndex];
        const scope = select.closest('[data-product-section]');
        const idInput = scope?.querySelector('input[name="id"]');
        const price = scope?.querySelector('[data-price] .price__current');
        const submit = scope?.querySelector('[data-product-submit]');
        if (idInput) idInput.value = option.value;
        if (price && option.dataset.price) price.textContent = option.dataset.price;
        if (submit) {
          submit.disabled = option.dataset.available !== 'true';
          submit.textContent = option.dataset.available === 'true' ? submit.dataset.addText : submit.dataset.soldText;
        }
      });
    });
  }

  bindSort() {
    document.querySelectorAll('[data-sort-by]').forEach((select) => {
      select.addEventListener('change', () => {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', select.value);
        window.location.href = url.toString();
      });
    });
  }

  bindFilterToggle() {
    document.addEventListener('click', (event) => {
      if (event.target.closest('[data-filter-toggle]')) document.querySelector('.filters')?.classList.toggle('is-open');
    });
  }

  bindStickyProduct() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-sticky-add]');
      if (!button) return;
      document.querySelector('.js-main-product-form')?.requestSubmit();
    });
  }

  bindBackLinks() {
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-back-link]');
      if (!link || !document.referrer) return;
      try {
        if (new URL(document.referrer).origin === window.location.origin) {
          event.preventDefault();
          window.history.back();
        }
      } catch (_) {}
    });
  }

  bindSiteFavicon() {
    const update = () => {
      const source = document.querySelector('[data-site-favicon]');
      if (!source) return;
      const faviconUrl = source.dataset.faviconUrl;
      const appleUrl = source.dataset.appleTouchIconUrl || faviconUrl;
      let favicon = document.querySelector('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.type = 'image/png';
      favicon.href = faviconUrl;
      let appleIcon = document.querySelector('link[rel="apple-touch-icon"]');
      if (!appleIcon) {
        appleIcon = document.createElement('link');
        appleIcon.rel = 'apple-touch-icon';
        document.head.appendChild(appleIcon);
      }
      appleIcon.href = appleUrl;
    };
    update();
    document.addEventListener('shopify:section:load', update);
  }
}

document.addEventListener('DOMContentLoaded', () => new GleamvoraTheme());
